import React from 'react';
import { Link } from 'react-router-dom';
import SectionHeader from '../components/ui/SectionHeader';
import FinalCTA from '../components/home/FinalCTA';

const highlights = [
  { icon: '📋', title: 'CSRD & ESRS', desc: 'Integrated double materiality assessment, IRO identification across all ESRS standards (E1–E5, S1–S4, G1), and NACE-linked disclosure scoping.' },
  { icon: '🌿', title: 'EU Taxonomy', desc: 'Activity-level revenue, CapEx, and OpEx collection. KPI calculation in the format required under the Taxonomy Disclosures Delegated Act. DNSH evidence documented.' },
  { icon: '⚖️', title: 'CBAM Compliance', desc: 'Supplier carbon data collection, EU default value management, quarterly declaration support, and certificate cost modelling for all covered goods categories.' },
  { icon: '🏭', title: 'Product Carbon Footprint', desc: 'ISO 14067-aligned PCF calculation with supplier data integration. Customer-ready disclosure documents for European tender responses.' },
  { icon: '📊', title: 'Corporate Carbon Footprint', desc: 'GHG Protocol-aligned Scope 1, 2, and 3 accounting with ESRS E1 disclosure outputs. All 15 Scope 3 categories supported.' },
  { icon: '🔗', title: 'Sustainable Procurement', desc: 'SAP Ariba integration, supplier ESG assessments, and Scope 3 Category 1 data collection — all in one platform.' },
];

export default function SolutionsEurope(): React.ReactElement {
  return (
    <main>
      <div className="page-hero">
        <div className="container">
          <span className="eyebrow" style={{ color: '#67E8F9', justifyContent: 'center', display: 'flex' }}>Solutions &mdash; Europe</span>
          <h1>CSRD &middot; EU Taxonomy &middot; CBAM &middot; ESRS. One platform.</h1>
          <p>
            For EU-based enterprises and non-EU companies with EU reporting obligations. From double
            materiality assessment through IRO-linked ESRS collection, EU Taxonomy KPI calculation,
            CBAM embedded carbon declarations, and Scope 3 supply chain data.
          </p>
          <div style={{ display: 'flex', gap: '16px', justifyContent: 'center', flexWrap: 'wrap' }}>
            <Link to="/contact" className="btn btn-primary">Book a demo</Link>
            <Link to="/platform" className="btn btn-ghost-light">See full platform</Link>
          </div>
        </div>
      </div>

      <section className="section-pad" style={{ background: 'var(--white)' }}>
        <div className="container">
          <SectionHeader
            eyebrow="Europe — CSRD / CBAM / EU Taxonomy"
            title="The full EU regulatory stack. One connected platform."
            description="Actula implements CSRD and EU Taxonomy as one integrated collection pathway — not two separate exercises. Data collected for ESRS standards is reused for Taxonomy KPI calculation where it overlaps."
          />

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '24px' }}>
            {highlights.map((h, i) => (
              <div key={i} className="card">
                <span style={{ fontSize: '1.5rem', display: 'block', marginBottom: '12px' }}>{h.icon}</span>
                <h4 style={{ color: 'var(--navy)', marginBottom: '8px' }}>{h.title}</h4>
                <p style={{ color: 'var(--slate-500)', fontSize: '0.9375rem', lineHeight: 1.65 }}>{h.desc}</p>
              </div>
            ))}
          </div>

          <div style={{ marginTop: '48px', background: 'var(--slate-50)', borderRadius: '12px', padding: '32px 40px', borderLeft: '4px solid var(--teal)' }}>
            <h3 style={{ color: 'var(--navy)', marginBottom: '16px' }}>Active modules for Europe</h3>
            <div className="solutions-modules">
              {['ESG Data Collection & Reporting', 'CBAM Compliance', 'Product Carbon Footprint', 'Corporate Carbon Footprint', 'Sustainable Procurement'].map(m => (
                <span key={m} className="solutions-module-tag">{m}</span>
              ))}
            </div>
          </div>
        </div>
      </section>

      <FinalCTA />
    </main>
  );
}
